import { goto } from '$app/navigation';
import { ApiService } from '$lib/services/api';
import {
	AOFL_PRODUCT_UUIDS,
	PAGES,
	GTM_EVENT_NAMES,
	COOKIES,
	LOCAL_STORAGE_KEYS,
	THIRD_PARTY_EVENTS,
	EXPERIMENT_ARMS
} from '$lib/utils/constants';
import { RegistrationPathService } from '$lib/services/registration-path';
import { MigrationService } from '$lib/services/migration';
import { get } from 'svelte/store';
import { UnknownError } from '../subscription/modules/errors';
import { publishFailure, publishSuccess, user, resetStore } from './store';
import { startLoading, stopLoading } from '$lib/stores/app/actions';
import * as Sentry from '@sentry/browser';
import Cookies from 'js-cookie';
import Logger from '$lib/services/analytics/events';
import { EventType } from '$lib/services/analytics/events/common';
import { getDeviceId } from '$lib/utils/device-id';
import { app } from '../app/store';
import { AnalyticsService } from '$lib/services/analytics';
import { v4 as uuidv4 } from 'uuid';
import { AppService } from '$lib/services/app';
import { isExperimentArmActive } from '$lib/services/experiments';
import {
	isAbcmouseLegacyUserIn2_0,
	switchExperimentToMatchUserState
} from '$lib/utils/cross-auth';

/**
 * @param {{ email: string; password: string; countryAbbrv: string; usernameType: string; }} credentialInfo - credentials
 * @param {{ redirect: boolean; logType?: EventType; }} options - options to handle this action
 * @returns {Promise<{data: import('./store').UserStoreState; error?: object}>} result
 */
export async function login(credentialInfo, options = { redirect: false }) {
	try {
		startLoading();
		const loginResp = await ApiService.login({
			username: credentialInfo.email,
			password: credentialInfo.password,
			country_abbrv: credentialInfo.countryAbbrv,
			username_type: credentialInfo.usernameType
		});

		// TODO: remove this when we test the login flow with the new experiment arm
		if (isExperimentArmActive('10004')) {
			if (isAbcmouseLegacyUserIn2_0(loginResp.roles)) {
				await logout({ meta: get(app).meta }, { redirect: false });
				await switchExperimentToMatchUserState(EXPERIMENT_ARMS.ABCMOUSE_2_0, EXPERIMENT_ARMS.ABCMOUSE_1_0);
				return await login(credentialInfo, {
					redirect: options.redirect
				});
			}
		}

		const userStatusResp = await ApiService.userGetStatus({
			username: credentialInfo.email,
			password: credentialInfo.password,
			country_abbrv: credentialInfo.countryAbbrv,
			username_type: credentialInfo.usernameType
		});

		await AnalyticsService.dataLayerPush({
			eventName: GTM_EVENT_NAMES.LOGIN,
			email: credentialInfo?.email
		});

		const newUserState = {
			...loginResp,
			...userStatusResp,
			password: credentialInfo.password,
			token_valid: true
		};
		publishSuccess(newUserState);

		if (options.logType) logEvent(options.logType, get(user));
		if (options.redirect) {
			const migrationResponse = await MigrationService.handleMigrationUser();
			if (migrationResponse) return { data: get(user), error: null };

			const path = RegistrationPathService.getRedirectPath(get(user));
			// TODO: make it platform (browser, SSR, in app) agnostic
			await goto(path);
		}

		return { data: get(user), error: null };
	} catch (error) {
		// TODO: Monitor error in Sentry, see if this is a good place to log error, or if its too noisy
		Sentry.captureException(error, {
			extra: {
				action: 'Error: Login Action',
				redirect: options.redirect
			}
		});
		let er = !(error instanceof Error) ? new UnknownError(error) : error;
		publishFailure(er);

		// TODO: remove this when we have a better way to handle this.
		const getUser = get(user);

		// TODO: remove this when we test the login flow with the new experiment arm
		if (isExperimentArmActive('10003') || isExperimentArmActive('10025')) {
			// This is a temporary fix for the error code 992004(Abcmouse 2 User login)
			if (getUser?.error
				&& getUser?.error?.errorCode
				&& getUser?.error?.errorCode === 992004
			) {
				await switchExperimentToMatchUserState(EXPERIMENT_ARMS.ABCMOUSE_1_0, EXPERIMENT_ARMS.ABCMOUSE_2_0);
				return await login(credentialInfo, options);
			}
		}

		return { data: getUser, error: er };
	} finally {
		stopLoading();
	}
}

/**
 * @param {{ username: string; password: string; countryAbbrv: string; usernameType: string; }} credentialInfo - credentials
 * @returns {Promise<object | unknown>} result
 */
export async function checkUserStatusWithCredentials(credentialInfo) {
	try {
		const userStatusResp = await ApiService.userGetStatus({
			username: credentialInfo.username,
			password: credentialInfo.password,
			country_abbrv: credentialInfo.countryAbbrv,
			username_type: credentialInfo.usernameType
		});

		const newUserState = { ...userStatusResp };
		publishSuccess(newUserState);
		return get(user);
	} catch (error) {
		// TODO: Monitor error in Sentry, see if this is a good place to log error, or if its too noisy
		Sentry.captureException(error, {
			extra: {
				action: 'Error: checkUserStatusWithCredentials Action'
			}
		});
		if (!(error instanceof Error)) {
			publishFailure(new UnknownError(error));
		} else {
			publishFailure(error);
		}
	}
}

/**
 * Validates the user.
 * @returns {Promise<object>} result
 */
export async function validateUser() {
	try {
		const response = await ApiService.validateLogin();
		const newUserState = {
			...response
		};
		publishSuccess(newUserState);
		return get(user);
	} catch (error) {
		// TODO: Monitor error in Sentry, see if this is a good place to log error, or if its too noisy
		Sentry.captureException(error, {
			extra: {
				action: 'Error: validateUser Action'
			}
		});
		if (!(error instanceof Error)) {
			publishFailure(new UnknownError(error));
		} else {
			publishFailure(error);
		}
		return get(user);
	}
}

/**
 * Logs out the user
 * @param {{ deviceCode: string; token: string }} deviceSessionInfo - appStore
 * @returns {Promise<{error: Error| null; data: object | null;}>} newUserState | error
 * @throws {UnknownError | APIError}
 */
export async function linkDeviceCodeWithSession(deviceSessionInfo) {
	try {
		const data = await ApiService.linkDeviceCodeWithSession(deviceSessionInfo);
		return { data, error: null };
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: Linking TV device code with active session',
				deviceCode: deviceSessionInfo.deviceCode
			}
		});
		console.error('linkDeviceCodeWithSession', error);
		let er = !(error instanceof Error) ? new UnknownError(error) : error;
		return { error: er, data: null };
	}
}

/**
 * Logs out the user
 * @param {import('$lib/stores/app/types').AppStore} appStore - appStore
 * @param {{ redirect: boolean }} options - options
 * @returns {Promise<void>} result
 */
export async function logout({ meta }, options = { redirect: true }) {
	try {
		return new Promise(async (resolve) => {
			startLoading();
			await ApiService.userLogout();
			Object.keys(Cookies.get()).forEach((cookieName) => {
				Cookies.remove(cookieName);
			});
			const experiments = localStorage.getItem(LOCAL_STORAGE_KEYS.EXPERIMENTS);
			localStorage.clear();
			if (experiments) {
				localStorage.setItem(LOCAL_STORAGE_KEYS.EXPERIMENTS, experiments);
			}
			resetStore();
			stopLoading();
			if (meta.inApp) {
				const appCallService = new AppService();
				await appCallService.logout();
			}
			resolve();
		});
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: Logout Action'
			}
		});
	} finally {
		// TODO: consider removing if use cases arise that dont want to go to login
		stopLoading();
		if (options.redirect) await goto(PAGES.INTERNAL.LOGIN);
	}
}

/**
 * Submits the users email
 * @param {{ email: string; countryAbbrv: string; usernameType: string; phone?: any; }} registerInfo - credentials
 * @param {{ redirect: boolean }} options - options to handle this action
 * @returns {Promise<object | unknown>} response
 */
export const registerEmail = async (registerInfo, options = { redirect: false }) => {
	try {
		// TODO: finish all entries like types and campaign tag.
		const response = await ApiService.prospectRegisterEmail({
			email_address: registerInfo?.email,
			prospect_type: '',
			language_locale: navigator.language,
			campaign_source_tag: '',
			registration_type: registerInfo?.regType,
			subscription_status: '',
			phone_number: registerInfo?.phone
		});

		// TODO: handle storing email in sessionStorage
		sessionStorage.setItem('tmpEmail', registerInfo.email);
		const newUserState = { tmpEmail: registerInfo.email };
		publishSuccess(newUserState);

		// TODO: move this to a transport, combine with datalayer push
		const sourceInfoJson = localStorage.getItem(LOCAL_STORAGE_KEYS.SOURCE_INFO);
		const sourceInfo = JSON.parse(sourceInfoJson) || {};
		const conversionEventId = uuidv4();
		AnalyticsService.trackThirdPartyConversion({
			event_type: THIRD_PARTY_EVENTS.INITIATE_CHECKOUT,
			email: registerInfo?.email,
			country_code: registerInfo?.countryAbbrv,
			conversion_event_id: conversionEventId,
			event_source: [],
			event_info: {
				...(sourceInfo?.values ? sourceInfo?.values : {}),
				fbc: Cookies.get(COOKIES.FBC),
				fbp: Cookies.get(COOKIES.FBP),
			}
		});
		await AnalyticsService.dataLayerPush({
			event_id: conversionEventId,
			eventName: GTM_EVENT_NAMES.PROSPECT_REG_SUBMITTED,
			email: registerInfo?.email,
			espRecipientID: response.esp_recipient_id,
			fbc: Cookies.get(COOKIES.FBC),
			fbp: Cookies.get(COOKIES.FBP),
		});

		return response;
	} catch (error) {
		// TODO: Monitor error in Sentry, see if this is a good place to log error, or if its too noisy
		Sentry.captureException(error, {
			extra: {
				action: 'Error: registerEmail Action'
			}
		});
		console.error(error);
	} finally {
		if (options.redirect) {
			goto(PAGES.INTERNAL.SUBSCRIPTION);
		}
	}
};

/**
 * @param {EventType} eventType - type of event
 * @param {import('./store').UserStoreState} userInfo - the user state
 * @returns {Promise<object>}
 */
async function logEvent(eventType, userInfo) {
	return await Logger.log({
		type: eventType,
		deviceId: getDeviceId(),
		legacyTrackCookie: Cookies.get(COOKIES.TRACK_COOKIE),
		meta: get(app).meta,
		aoflProductId: AOFL_PRODUCT_UUIDS.ABCMOUSE,
		detail: {
			udid: localStorage.getItem(LOCAL_STORAGE_KEYS.UDID) ?? undefined,
			adid: localStorage.getItem(LOCAL_STORAGE_KEYS.ADID) ?? undefined,
			firstTimeLogin: userInfo.isFirstLogin,
			userToProductId: userInfo.userToProductUuid
		}
	});
}

/**
 * @param {{ token: string; email: string; password: string; countryAbbrv: string; usernameType: string; }} credentialInfo - credentials
 * @param {{ redirect: boolean; logType?: EventType; }} options - options to handle this action
 * @returns {Promise<{data: import('./store').UserStoreState; error?: object}>} result
 */
export async function userResetPassword(credentialInfo, options = { redirect: false }) {
	try {
		startLoading();
		const response = await ApiService.userResetPassword(credentialInfo);

		if (options.redirect) await goto(PAGES.INTERNAL.LOGIN);
		return { data: response, error: undefined };
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: userResetPassword Action'
			}
		});
		let er = !(error instanceof Error) ? new UnknownError(error) : error;
		publishFailure(er);
		return { data: get(user), error: er };
	} finally {
		stopLoading();
	}
}
